import { FC, MouseEvent, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Divider, IconButton, Popover, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DifferenceIcon from '@mui/icons-material/Difference';
import { DateCalendar } from '@mui/x-date-pickers-pro';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { localeFormatterHelper } from '../../../../../../shared/helpers/formatter/localeFormatter.helper.ts';
import { comparisonMetricsState } from './comparisonMetrics.state.ts';
import { MAX_COMPARISON_DATES } from './comparison.types.ts';

export const ComparisonControl: FC = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [dates, setDates] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const sub = comparisonMetricsState.sub.state().subscribe((s) => {
      setVisible(s.visible);
      setDates(s.dates);
    });
    return () => sub.unsubscribe();
  }, []);

  const calendarValue = useMemo(
    () => (dates.length ? parseISO(dates[dates.length - 1]) : null),
    [dates],
  );

  const openCalendar = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const closeCalendar = () => setAnchorEl(null);

  return (
    <Stack direction='row' alignItems='center' spacing={1} sx={{ minWidth: 0 }}>
      <Divider orientation='vertical' flexItem sx={{ my: 1 }} />
      <Button
        color='primary'
        startIcon={<DifferenceIcon />}
        onClick={() => comparisonMetricsState.pub.toggleVisible()}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        {t('order.compare_to')}
      </Button>
      {visible && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            overflowX: 'auto',
            py: 0.5,
          }}
        >
          {dates.map((date) => (
            <Chip
              key={date}
              size='small'
              label={localeFormatterHelper.formatDate(date)}
              onDelete={() => comparisonMetricsState.pub.removeDate(date)}
              sx={{ flexShrink: 0 }}
            />
          ))}
        </Box>
      )}
      {visible && dates.length < MAX_COMPARISON_DATES && (
        <IconButton
          color='primary'
          size='small'
          title={t('order.comparison_add_date')}
          aria-label={t('order.comparison_add_date')}
          onClick={openCalendar}
          sx={{ flexShrink: 0 }}
        >
          <AddIcon />
        </IconButton>
      )}
      <Popover
        open={visible && Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeCalendar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <DateCalendar
          value={calendarValue}
          disableFuture
          onChange={(date) => {
            if (date) {
              comparisonMetricsState.pub.addDate(format(date, 'yyyy-MM-dd'));
              closeCalendar();
            }
          }}
        />
      </Popover>
    </Stack>
  );
};
